import { useCallback, useSyncExternalStore } from "react";

/**
 * A per-browser preference in localStorage (the render-mode switch, the editor choice, the editor's view mode): one key,
 * one `window` event so every open screen follows a change in this tab, the `storage` event for other tabs, and an
 * in-memory fallback while storage is unavailable (private mode, blocked storage, quota), so the control still works
 * until reload. The default value clears the key.
 */
export interface StoredPreference<T extends string> {
    /** The stored value; the default when unset, unknown or unreadable. */
    get(): T;
    /** Stores the value (the default clears the key) and tells every open screen. */
    set(value: T): void;
    /** Calls `listener` when the value changes in this tab or in another one. */
    subscribe(listener: () => void): () => void;
    /** `[value, setValue]`, re-rendering whenever the value changes anywhere. */
    use(): [T, (value: T) => void];
}

function storage(): Storage | null {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
        return null;
    }
}

export function createStoredPreference<T extends string>(key: string, event: string, parse: (value: unknown) => T, fallback: T): StoredPreference<T> {
    let memory: T = fallback;
    /** Set once a write failed: reads then come from memory, or the old stored value would win over the control. */
    let broken = false;

    const get = (): T => {
        try {
            const store = storage();
            if (!store || broken) return memory;
            const value = store.getItem(key);
            return value === null ? fallback : parse(value);
        } catch {
            return memory;
        }
    };

    const set = (value: T): void => {
        const next = parse(value);
        memory = next;
        try {
            const store = storage();
            if (next === fallback) store?.removeItem(key);
            else store?.setItem(key, next);
            broken = false;
        } catch {
            broken = true;
        }
        if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(event, { detail: { value: next } }));
    };

    const subscribe = (listener: () => void): (() => void) => {
        if (typeof window === "undefined") return () => {};
        const onStorage = (change: StorageEvent) => {
            if (change.key === null || change.key === key) listener();
        };
        window.addEventListener(event, listener);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(event, listener);
            window.removeEventListener("storage", onStorage);
        };
    };

    const use = (): [T, (value: T) => void] => {
        const value = useSyncExternalStore(subscribe, get, () => fallback);
        const setValue = useCallback((next: T) => set(next), []);
        return [value, setValue];
    };

    return { get, set, subscribe, use };
}
